import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { criarRecebimento } from '@/integrations/supabase/recebimentos-pedidos';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { getTodayAsISO } from '@/utils/dateOnly';

interface DialogRegistrarRecebimentoProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  saldoDevedor: number;
  onRecebimentoCriado?: () => void;
}

export function DialogRegistrarRecebimento({
  isOpen,
  onClose,
  orderId,
  saldoDevedor,
  onRecebimentoCriado
}: DialogRegistrarRecebimentoProps) {
  const [valor, setValor] = useState('');
  const [dataRecebimento, setDataRecebimento] = useState(getTodayAsISO());
  const [formaPagamento, setFormaPagamento] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!valor || parseFloat(valor) <= 0) {
      toast.error('Valor deve ser maior que zero');
      return;
    }

    if (parseFloat(valor) > saldoDevedor) {
      toast.error(`Valor não pode ser maior que o saldo devedor (R$ ${saldoDevedor.toFixed(2)})`);
      return;
    }

    if (!dataRecebimento) {
      toast.error('Data de recebimento obrigatória');
      return;
    }

    setIsLoading(true);
    try {
      const result = await criarRecebimento({
        order_id: orderId,
        valor_recebido: parseFloat(valor),
        data_recebimento: dataRecebimento,
        forma_pagamento: formaPagamento || undefined,
        observacoes: observacoes || undefined
      });

      if (result.ok) {
        toast.success(`Recebimento de R$ ${valor} registrado com sucesso!`);
        setValor('');
        setDataRecebimento(getTodayAsISO());
        setFormaPagamento('');
        setObservacoes('');
        onRecebimentoCriado?.();
        onClose();
      } else {
        toast.error(result.error || 'Erro ao registrar recebimento');
      }
    } catch (erro: any) {
      toast.error(erro.message || 'Erro ao registrar recebimento');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="border-purple-200">
        <DialogHeader>
          <DialogTitle className="text-purple-900 text-xl">💰 Registrar Recebimento</DialogTitle>
        </DialogHeader>

        <div className="mt-2 p-3 rounded-lg bg-purple-50 border border-purple-200">
          <p className="text-sm font-medium text-purple-700">Saldo devedor:</p>
          <p className="text-lg font-bold text-purple-900">R$ {saldoDevedor.toFixed(2)}</p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="valor-recebimento" className="text-purple-900 font-semibold">Valor (R$)</Label>
            <Input
              id="valor-recebimento"
              type="number"
              step="0.01"
              min="0"
              max={saldoDevedor}
              placeholder="0,00"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              className="border-purple-200 focus:border-purple-500 focus:ring-purple-500"
            />
            {valor && parseFloat(valor) > 0 && (
              <div className={`text-xs font-medium p-2 rounded-lg flex items-center gap-2 ${
                parseFloat(valor) <= saldoDevedor
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-red-50 text-red-700'
              }`}>
                {parseFloat(valor) <= saldoDevedor ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Saldo restante: R$ {(saldoDevedor - parseFloat(valor)).toFixed(2)}
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4" />
                    Valor excede o saldo devedor
                  </>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="data-recebimento" className="text-purple-900 font-semibold">Data do Recebimento</Label>
            <Input
              id="data-recebimento"
              type="date"
              value={dataRecebimento}
              onChange={(e) => setDataRecebimento(e.target.value)}
              className="border-purple-200 focus:border-purple-500 focus:ring-purple-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="forma-pagamento" className="text-purple-900 font-semibold">Forma de Pagamento (opcional)</Label>
            <Select value={formaPagamento} onValueChange={setFormaPagamento}>
              <SelectTrigger className="border-purple-200 focus:border-purple-500 focus:ring-purple-500">
                <SelectValue placeholder="Selecione a forma de pagamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pix">📱 Pix</SelectItem>
                <SelectItem value="dinheiro">💵 Dinheiro</SelectItem>
                <SelectItem value="cartao-debito">🏦 Cartão de Débito</SelectItem>
                <SelectItem value="cartao-credito">💳 Cartão de Crédito</SelectItem>
                <SelectItem value="transferencia">🏧 Transferência</SelectItem>
                <SelectItem value="boleto">📋 Boleto</SelectItem>
                <SelectItem value="cheque">✉️ Cheque</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="obs-recebimento" className="text-purple-900 font-semibold">Observações (opcional)</Label>
            <Textarea
              id="obs-recebimento"
              placeholder="Ex: Devolveu 10 de taxa, cliente confirmou..."
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
              className="resize-none border-purple-200 focus:border-purple-500 focus:ring-purple-500"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading} className="border-purple-200">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !valor || parseFloat(valor) <= 0 || parseFloat(valor) > saldoDevedor}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Registrando...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Registrar Recebimento
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
